/* THE COUNTER — <counter-recs>: Rebuy picks, Counter cards, for the cart drawer and /cart.
   Same division as the PDP routine band (sections/counter-routine.liquid), packaged as an element
   because the drawer is RE-RENDERED after every cart change (counter-cart.js swaps the children
   of <cart-drawer>). So:
   - Rebuy's widget renders into a SOURCE outside <cart-drawer> (#cc-rebuy-source-<section>),
     which the swap never touches, so Rebuy's widget survives every re-render.
   - Every new <counter-recs> reads the handles Rebuy rendered there, drops what is already in the
     bag, and draws Counter cards (sections/counter-rec-card) — from a page-level CACHE after the
     first fetch, so a re-render is instant.
   - Add to bag carries Rebuy's attribution (_attribution / _source / _widget_id).
   - Starts hidden ([data-empty]) and only shows once at least one card exists. It FADES in the
     first time it appears on a page (240ms); re-renders after that are instant, never re-fade.

   IN-CART QUICK ADD (Sept 26 2026, founder): every variant case adds FROM THE CART — never a
   trip to the PDP, which takes the shopper backwards. "Select shade" / "Choose options" on a
   rec card, and "Select …" on an empty-bag row, open sections/counter-quick-add INLINE under
   the row: shade unselected, other options preselected, Add enabled only once fully specified.
   Rec-card adds keep Rebuy's attribution; empty-bag adds carry none. counter-cart.js sees the
   add and re-renders the drawer (which closes the panel).

   KITS WITH 2+ CHOICES (Sept 27 2026, founder: "add the kit from the panel without taking the
   user to the kit PDP"). "Choose options" on a kit card opens the same panel on EVERY card
   surface: "Pairs well with" in the cart, the empty bag's cards (<counter-empty-cards>) and the
   PDP routine band ([data-counter-routine-mount]). The panel is appended to its band so it spans
   the band under the cards (capped at 560px on the PDP). Routine-band adds carry that band's
   Rebuy widget id and open the drawer. When a kit has two shade choices the button names the
   missing one ("Select lip shade"), never a bare "Select shade". This file reaches every page
   through counter-empty-cards.js, which the drawer always loads.

   REBUY MUST BE TOLD THE BAG CHANGED: counter-cart.js's own adds bypass Rebuy's fetch hook, so
   whenever a band connects with a new bag we call `Rebuy.Cart.fetchShopifyCart()`.

   SHARED SOURCE (13 Oct 2026): on /cart the page band (Rebuy app block) stayed empty while the
   drawer band on the same page, mounted on the same widget 312463, filled. Both bands now read
   from ANY `.cc-rebuy-source` on the page: their own first, then whichever source Rebuy did
   fill. Same widget, same picks, so the page band shows what the drawer's source received.

   Debug: counterRecsDebug() */
(() => {
  if (window.__counterRecs) return;
  window.__counterRecs = true;
  const T = (k, f) => (window.CounterStrings && window.CounterStrings[k]) || f;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const theme = window.Shopify && Shopify.theme;
  const previewQs = theme && theme.role && theme.role !== 'main' && theme.id ? '&preview_theme_id=' + theme.id : '';
  const reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cache = new Map();
  const qaCache = new Map();
  const log = [];
  const note = m => { log.push(m); if (log.length > 30) log.shift(); };
  let revealed = false;

  const css = document.createElement('style');
  css.textContent = `
.cc-rebuy.cc-rebuy--drawer{padding-top:32px!important}
@keyframes crcFade{from{opacity:0}to{opacity:1}}
counter-recs[data-fade]{animation:crcFade 240ms cubic-bezier(.4,0,.2,1)}
.cqa{animation:crcFade 150ms cubic-bezier(.4,0,.2,1)}
@media (prefers-reduced-motion:reduce){counter-recs[data-fade],.cqa{animation:none}}
.cqa{margin-top:14px;padding:16px;border:1px solid rgba(76,48,65,.12);background:#fff;display:flex;flex-direction:column;gap:14px;text-align:left}
[data-counter-routine-mount]>.cqa{max-width:560px;margin-top:24px}
.cqa__head{display:grid;grid-template-columns:1fr auto;gap:2px 12px;align-items:start}
.cqa__name{margin:0;font-size:13px;line-height:1.35;color:#4C3041}
.cqa__price{margin:0;grid-column:1;font-size:13px;font-weight:300;color:#4C3041}
.cqa__close{grid-column:2;grid-row:1/span 2;width:36px;height:36px;margin:-10px -10px 0 0;display:grid;place-items:center;border:0;background:none;color:#4C3041;cursor:pointer}
.cqa__set{margin:0;padding:0;border:0;min-width:0}
.cqa__label{padding:0;margin-bottom:6px;font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:#4C3041}
.cqa__label span{font-weight:400;letter-spacing:.04em;text-transform:none}
.cqa__values{display:flex;flex-wrap:wrap;align-items:center;gap:2px 6px}
.cqa__sw,.cqa__opt{position:relative;display:grid;place-items:center;cursor:pointer}
.cqa__sw{width:44px;height:44px}
.cqa__radio{position:absolute;inset:0;margin:0;opacity:0;cursor:pointer}
.cqa__radio:disabled{cursor:default}
.cqa__radio:checked+.counter-swatch{box-shadow:0 0 0 2px #fff,0 0 0 3.5px #4C3041!important}
.cqa .crc__swimg{width:28px;height:28px}
.cqa__radio:checked+.crc__swimg{box-shadow:0 0 0 2px #fff,0 0 0 3.5px #4C3041}
.cqa__chip{display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border:1px solid rgba(76,48,65,.2);border-radius:2px;font-size:13px;color:#4C3041;white-space:nowrap}
.cqa__chip[hidden]{display:none}
.cqa__radio:checked~.cqa__chip{border:1.5px solid #4C3041;padding:0 11.5px}
.cqa__radio:disabled~*{opacity:.4}
.cqa__radio:focus-visible~*{outline:2px solid #A35F6E;outline-offset:3px}
.cqa__add{display:flex;align-items:center;justify-content:center;width:100%;min-height:44px;margin:0;padding:12px 16px;border:0;border-radius:2px;background:#4C3041;color:#fff;font-family:Inter,sans-serif;font-size:11px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;cursor:pointer;transition:background-color 150ms cubic-bezier(.4,0,.2,1)}
.cqa__add:hover:not(:disabled){background:#A35F6E}
.cqa__add:disabled{opacity:.4;cursor:default}
.cqa__add:focus-visible{outline:none;box-shadow:0 0 0 2px #fff,0 0 0 4px #A35F6E}
.cc-tile+.cqa,.cc-rrow+.cqa{margin-bottom:8px}
`;
  document.head.appendChild(css);

  // ---- Smart Cart guard ----
  const openCounterDrawer = () => {
    const d = document.getElementById('cart-drawer');
    if (d && typeof d.show === 'function') { try { d.show(); return; } catch (e) {} }
    const opener = document.querySelector('[aria-controls="cart-drawer"]');
    if (opener) opener.click();
  };

  // ---- Tell Rebuy the bag changed ----
  let toldRebuy = null;
  let rebuySyncs = 0;
  const syncRebuy = (key, tries = 0) => {
    if (key === toldRebuy) return;
    const C = window.Rebuy && Rebuy.Cart;
    if (!C || typeof C.fetchShopifyCart !== 'function') {
      if (tries < 10) setTimeout(() => syncRebuy(key, tries + 1), 500);
      else note('Rebuy.Cart unavailable');
      return;
    }
    toldRebuy = key;
    rebuySyncs++;
    try { C.fetchShopifyCart(() => note('rebuy cart synced ' + key)); } catch (e) { note('rebuy sync failed'); }
  };

  const cleanHref = href => {
    try { const u = new URL(href, location.origin); u.searchParams.delete('variant'); return u.pathname + u.search; } catch (e) { return href; }
  };
  const handleOf = href => { const m = (href || '').match(/\/products\/([^?#\/]+)/); return m ? decodeURIComponent(m[1]) : null; };
  const readPicks = (source, skip) => {
    const seen = new Set(); const out = [];
    source.querySelectorAll('.rebuy-product-block').forEach(b => {
      const a = b.querySelector('a.rebuy-product-title-link') || b.querySelector('a[href*="/products/"]');
      if (!a) return;
      const h = handleOf(a.getAttribute('href'));
      if (!h || skip.has(h) || seen.has(h)) return;
      seen.add(h); out.push({ h, href: cleanHref(a.getAttribute('href')) });
    });
    return out;
  };
  const sectionHtml = (map, h, id, sel) => {
    if (map.has(h)) return map.get(h);
    const p = fetch(`${root}products/${encodeURIComponent(h)}?section_id=${id}${previewQs}`, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.text() : ''))
      .then(html => { const c = new DOMParser().parseFromString(html, 'text/html').querySelector(sel); return c ? c.outerHTML : ''; })
      .catch(() => '');
    map.set(h, p);
    return p;
  };
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const priceHtml = v => (v.c ? `<s class="crc__was">${esc(v.c)}</s> <span class="crc__now">${esc(v.p)}</span>` : esc(v.p));
  const addToBag = async (id, rebuyWidget) => {
    const item = { id, quantity: 1 };
    if (rebuyWidget) item.properties = { _attribution: 'Rebuy Product', _source: 'Rebuy', _widget_id: String(rebuyWidget) };
    const r = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
    if (!r.ok) throw new Error('add ' + r.status);
    note('added ' + id);
  };

  // ---- In-cart quick add ----
  const QA_TRIGGER = [
    'counter-recs .crc__btn--pdp',
    'counter-recs .crc[data-case="kit"] a.crc__btn',
    'counter-empty-cards .crc[data-case="kit"] a.crc__btn',
    '[data-counter-routine-mount] .crc[data-case="kit"] a.crc__btn',
    '.cc-rrow__act:not([data-cc-add])',
    '.cc-tile__add:not([data-cc-add])'
  ].join(',');
  const closePanels = () => document.querySelectorAll('[data-cqa]').forEach(p => {
    if (p._trigger) p._trigger.setAttribute('aria-expanded', 'false');
    p.remove();
  });
  const wirePanel = (panel, trigger, rebuyWidget) => {
    let vs = [];
    try { vs = JSON.parse(panel.querySelector('[data-cqa-variants]').textContent); } catch (e) {}
    const sets = [...panel.querySelectorAll('[data-cqa-option]')];
    const btn = panel.querySelector('[data-cqa-add]');
    const price = panel.querySelector('[data-cqa-price]');
    let current = null;
    const update = () => {
      const chosen = sets.map(s => { const r = s.querySelector('.cqa__radio:checked'); return r ? r.value : null; });
      sets.forEach((s, i) => { const lab = s.querySelector('[data-cqa-chosen]'); if (lab) lab.textContent = chosen[i] ? ': ' + chosen[i] : ''; });
      const missing = sets.findIndex((s, i) => chosen[i] == null);
      current = missing === -1 ? vs.find(v => v.o.every((val, i) => val === chosen[i])) : null;
      if (missing !== -1) {
        const isShade = sets[missing].dataset.shade === 'true' && sets.length === 1;
        btn.disabled = true; btn.textContent = isShade ? T('select_shade', 'Select shade') : T('select_option', 'Select %option%').replace('%option%', (sets[missing].querySelector('.cqa__label').firstChild.textContent || 'option').toLowerCase());
      } else if (!current) { btn.disabled = true; btn.textContent = T('unavailable', 'Unavailable'); }
      else if (!current.a) { btn.disabled = true; btn.textContent = T('sold_out', 'Sold out'); price.innerHTML = priceHtml(current); }
      else { btn.disabled = false; btn.textContent = T('add', 'Add to bag'); price.innerHTML = priceHtml(current); }
    };
    panel.addEventListener('change', update);
    panel.querySelector('[data-cqa-close]').addEventListener('click', () => { trigger.setAttribute('aria-expanded', 'false'); panel.remove(); trigger.focus(); });
    btn.addEventListener('click', async () => {
      if (!current || btn.disabled) return;
      btn.disabled = true; btn.textContent = T('adding', 'Adding…');
      try {
        await addToBag(current.id, rebuyWidget);
        if (trigger.closest('.cc-page')) { location.reload(); return; }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        trigger.setAttribute('aria-expanded', 'false');
        panel.remove();
        if (trigger.closest('[data-counter-routine-mount]')) openCounterDrawer();
      } catch (err) { btn.disabled = false; btn.textContent = T('try_again', 'Try again'); note('quick add failed ' + err.message); }
    });
    update();
  };
  document.addEventListener('click', async e => {
    const trigger = e.target.closest(QA_TRIGGER);
    if (!trigger) return;
    const card = trigger.closest('.crc');
    if (card && card.dataset.case === 'soldout') return;
    const h = (card && card.dataset.handle) || handleOf(trigger.getAttribute('href'));
    if (!h) return;
    e.preventDefault();
    e.stopPropagation();
    const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
    closePanels();
    if (wasOpen) return;
    trigger.setAttribute('aria-busy', 'true');
    const html = await sectionHtml(qaCache, h, 'counter-quick-add', '[data-cqa]');
    trigger.removeAttribute('aria-busy');
    if (!html) { note('quick add unavailable ' + h); location.href = trigger.getAttribute('href') || `${root}products/${h}`; return; }
    const t = document.createElement('template'); t.innerHTML = html;
    const panel = t.content.firstElementChild;
    panel._trigger = trigger;
    const recs = trigger.closest('counter-recs');
    const emptyCards = trigger.closest('counter-empty-cards');
    const routine = trigger.closest('[data-counter-routine-mount]');
    const row = trigger.closest('.cc-rrow');
    const tile = trigger.closest('.cc-tile');
    if (recs) recs.appendChild(panel);
    else if (emptyCards) emptyCards.appendChild(panel);
    else if (routine) routine.appendChild(panel);
    else if (row) row.after(panel);
    else if (tile) (tile.parentElement || tile).after(panel);
    else trigger.after(panel);
    trigger.setAttribute('aria-expanded', 'true');
    let widget = null;
    if (recs) widget = recs.dataset.widget || null;
    else if (routine) { const el = routine.querySelector('[data-rebuy-id]'); widget = (el && el.dataset.rebuyId) || null; }
    wirePanel(panel, trigger, widget);
    const first = panel.querySelector('.cqa__radio:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  }, true);

  class CounterRecs extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this._on = true;
      this.rail = this.querySelector('[data-crc-rail]');
      this.nav = this.querySelector('[data-crc-nav]');
      this.source = document.getElementById(this.dataset.source);
      this.skip = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
      if (!this.rail || !this.source) { note('missing rail/source'); return; }
      syncRebuy(this.dataset.productIds || '');
      this.mo = new MutationObserver(() => { clearTimeout(this.t); this.t = setTimeout(() => this.build(), 300); });
      new Set([this.source, ...document.querySelectorAll('.cc-rebuy-source')]).forEach(s => this.mo.observe(s, { childList: true, subtree: true }));
      this.build();
      this.wire();
    }
    disconnectedCallback() { if (this.mo) this.mo.disconnect(); }
    pickSource() {
      const filled = s => s && s.querySelector('.rebuy-product-block');
      if (filled(this.source)) return this.source;
      const other = [...document.querySelectorAll('.cc-rebuy-source')].find(filled);
      if (other) note('using source ' + other.id);
      return other || this.source;
    }
    async build() {
      const picks = readPicks(this.pickSource(), this.skip);
      const key = picks.map(p => p.h).join(',');
      if (key === this._key) return;
      this._key = key;
      if (!picks.length) { this.setAttribute('data-empty', ''); return; }
      const htmls = await Promise.all(picks.map(p => sectionHtml(cache, p.h, 'counter-rec-card', '[data-crc]')));
      if (key !== this._key) return;
      const frag = document.createDocumentFragment();
      htmls.forEach((html, i) => {
        if (!html) return;
        const t = document.createElement('template'); t.innerHTML = html;
        const card = t.content.firstElementChild;
        card.setAttribute('role', 'listitem');
        card.querySelectorAll('[data-crc-link]').forEach(a => a.setAttribute('href', picks[i].href));
        frag.appendChild(card);
      });
      this.rail.replaceChildren(frag);
      if (this.rail.children.length) {
        if (!revealed) { this.setAttribute('data-fade', ''); revealed = true; }
        this.removeAttribute('data-empty'); this.rail.hidden = false;
      } else this.setAttribute('data-empty', '');
      this.updateNav();
    }
    pageWidth() {
      const c = this.rail.querySelector('[data-crc]');
      if (!c) return this.rail.clientWidth;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      const w = c.getBoundingClientRect().width + gap;
      return Math.max(1, Math.floor((this.rail.clientWidth + gap) / w)) * w;
    }
    updateNav() {
      if (!this.nav) return;
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      this.nav.hidden = max <= 2;
      const [prev, next] = this.nav.querySelectorAll('button');
      if (prev) prev.disabled = this.rail.scrollLeft <= 2;
      if (next) next.disabled = this.rail.scrollLeft >= max - 2;
    }
    wire() {
      if (this.nav) {
        const [prev, next] = this.nav.querySelectorAll('button');
        const go = d => this.rail.scrollBy({ left: d * this.pageWidth(), behavior: reduce ? 'auto' : 'smooth' });
        if (prev) prev.addEventListener('click', () => go(-1));
        if (next) next.addEventListener('click', () => go(1));
      }
      let raf;
      this.rail.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.updateNav()); }, { passive: true });
      this.rail.addEventListener('change', e => {
        const radio = e.target.closest('.crc__radio');
        if (!radio) return;
        const card = radio.closest('[data-crc]');
        let vs = [];
        try { vs = JSON.parse(card.querySelector('[data-crc-variants]').textContent); } catch (err) {}
        const v = vs.find(x => x.id === +radio.value);
        if (!v) return;
        card.dataset.variant = v.id;
        const price = card.querySelector('[data-crc-price]');
        if (price) price.innerHTML = priceHtml(v);
        const label = card.querySelector('[data-crc-label]');
        if (label && card.dataset.case === 'shade') label.textContent = v.o;
        const btn = card.querySelector('[data-crc-add]');
        if (btn) { btn.disabled = !v.a; btn.textContent = v.a ? T('add', 'Add to bag') : T('sold_out', 'Sold out'); }
      });
      this.rail.addEventListener('click', async e => {
        const btn = e.target.closest('[data-crc-add]');
        if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
        e.preventDefault();
        const card = btn.closest('[data-crc]');
        const id = +card.dataset.variant;
        if (!id) return;
        btn.setAttribute('aria-busy', 'true'); btn.disabled = true; btn.textContent = T('adding', 'Adding…');
        try {
          await addToBag(id, this.dataset.widget);
          if (this.closest('.cc-page')) { location.reload(); return; }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch (err) {
          btn.removeAttribute('aria-busy'); btn.disabled = false; btn.textContent = T('try_again', 'Try again');
        }
      });
    }
  }
  if (!customElements.get('counter-recs')) customElements.define('counter-recs', CounterRecs);

  window.counterRecsDebug = () => ({
    rebuySyncs,
    toldRebuy,
    smartCartPresent: !!(window.Rebuy && Rebuy.SmartCart),
    openPanels: document.querySelectorAll('[data-cqa]').length,
    bands: [...document.querySelectorAll('counter-recs')].map(el => ({
      widget: el.dataset.widget,
      source: !!el.source,
      rebuyProducts: el.source ? el.source.querySelectorAll('.rebuy-product-block').length : 0,
      readingFrom: el.pickSource ? (el.pickSource() || {}).id : null,
      excluded: [...(el.skip || [])],
      cards: el.rail ? el.rail.children.length : 0,
      empty: el.hasAttribute('data-empty')
    })),
    rebuyLoaded: !!(window.Rebuy && typeof Rebuy.init === 'function'),
    log: log.slice()
  });
})();
